// CEO Reports - Main Page Component
// Refactored into modular structure

import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { RefreshCw, Download } from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

// Import local components
import { GlossaryDialog, ReportNav } from './components';
import { reportCategories, endpoints } from './utils/reportConfig';

// Import all tab components
import {
  ExecutiveSummaryTab,
  DailyFundingTab,
  DividendInOutTab,
  PnLBySymbolTab,
  ClientProfitabilityTab,
  CommissionAnalyticsTab,
  DepositFlowTab,
  BookAnalysisTab,
  HedgingEfficiencyTab,
  RegulatoryCapitalTab,
  SuspiciousActivityTab,
  MarginCallsTab,
  FinancialStatementTab,
  BalanceSheetTab,
  ExecutionQualityTab,
  ExitValuationTab,
  InvestorReportTab
} from './AllTabs';

const CEOReports = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('executive');
  const [loading, setLoading] = useState({});
  const [data, setData] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [glossary, setGlossary] = useState(null);
  const [navCollapsed, setNavCollapsed] = useState(false);

  const isAdmin = user?.role === 'admin';

  // Fetch glossary on mount
  useEffect(() => {
    const fetchGlossary = async () => {
      try {
        const response = await api.get('/api/ceo-reports/glossary');
        setGlossary(response.data);
      } catch (error) {
        console.error('Failed to fetch glossary:', error);
      }
    };
    if (isAdmin) fetchGlossary();
  }, [isAdmin]);

  const fetchReport = useCallback(async (reportType) => {
    if (!isAdmin) return;
    
    setLoading(prev => ({ ...prev, [reportType]: true }));
    try {
      const response = await api.get(endpoints[reportType]);
      setData(prev => ({ ...prev, [reportType]: response.data }));
    } catch (error) {
      console.error(`Error fetching ${reportType} report:`, error);
      toast.error(`Failed to load ${reportType} report`);
    } finally {
      setLoading(prev => ({ ...prev, [reportType]: false }));
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      fetchReport(activeTab);
    }
  }, [activeTab, isAdmin, fetchReport]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchReport(activeTab);
    setRefreshing(false);
  };

  const handleExport = async (reportType) => {
    try {
      const reportData = data[reportType];
      if (!reportData) {
        toast.error('No data to export');
        return;
      }
      
      const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportType}_report_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Report exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export report');
    }
  };

  const getCurrentReportTitle = () => {
    for (const category of reportCategories) {
      const report = category.reports.find(r => r.id === activeTab);
      if (report) return report.label;
    }
    return 'Report';
  };

  const renderReportContent = () => {
    const reportComponents = {
      executive: <ExecutiveSummaryTab data={data.executive} loading={loading.executive} glossary={glossary} />,
      dailyfunding: <DailyFundingTab data={data.dailyfunding} loading={loading.dailyfunding} glossary={glossary} />,
      dividendinout: <DividendInOutTab data={data.dividendinout} loading={loading.dividendinout} glossary={glossary} />,
      pnlsymbol: <PnLBySymbolTab data={data.pnlsymbol} loading={loading.pnlsymbol} glossary={glossary} />,
      clientprofit: <ClientProfitabilityTab data={data.clientprofit} loading={loading.clientprofit} glossary={glossary} />,
      commission: <CommissionAnalyticsTab data={data.commission} loading={loading.commission} glossary={glossary} />,
      depositflow: <DepositFlowTab data={data.depositflow} loading={loading.depositflow} glossary={glossary} />,
      books: <BookAnalysisTab data={data.books} loading={loading.books} glossary={glossary} />,
      hedging: <HedgingEfficiencyTab data={data.hedging} loading={loading.hedging} glossary={glossary} />,
      regulatory: <RegulatoryCapitalTab data={data.regulatory} loading={loading.regulatory} glossary={glossary} />,
      suspicious: <SuspiciousActivityTab data={data.suspicious} loading={loading.suspicious} glossary={glossary} />,
      margincalls: <MarginCallsTab data={data.margincalls} loading={loading.margincalls} glossary={glossary} />,
      financial: <FinancialStatementTab data={data.financial} loading={loading.financial} glossary={glossary} />,
      balance: <BalanceSheetTab data={data.balance} loading={loading.balance} glossary={glossary} />,
      execution: <ExecutionQualityTab data={data.execution} loading={loading.execution} glossary={glossary} />,
      valuation: <ExitValuationTab data={data.valuation} loading={loading.valuation} glossary={glossary} />,
      investor: <InvestorReportTab data={data.investor} loading={loading.investor} glossary={glossary} />,
    };
    return reportComponents[activeTab] || <div className="text-center py-8">Select a report</div>;
  };

  return (
    <div className="flex h-[calc(100vh-120px)] -m-6" data-testid="ceo-reports-page">
      {/* Sidebar Navigation */}
      <ReportNav 
        activeTab={activeTab} 
        onTabChange={setActiveTab}
        collapsed={navCollapsed}
        onToggleCollapse={() => setNavCollapsed(!navCollapsed)}
      />
      
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Compact Header */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 bg-black/20">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-semibold">{getCurrentReportTitle()}</h1>
            <span className="text-xs text-muted-foreground">CEO Analytics</span>
          </div>
          <div className="flex items-center gap-2">
            <GlossaryDialog glossary={glossary} />
            <Button variant="ghost" size="sm" onClick={handleRefresh} disabled={refreshing} className="h-8">
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => handleExport(activeTab)} className="h-8">
              <Download className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {/* Report Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {renderReportContent()}
        </div>
      </div>
    </div>
  );
};

export default CEOReports;
